import type React from "react";
import { twJoin } from "tailwind-merge";

import type { UserDto } from "@/api/types";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import type { CommentType } from "@/components/Ticket/TicketCommentField/TicketCommentFieldPure";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import {
  MAXIMUM_DOCUMENT_FILE_SIZE_IN_MEGA_BYTES,
  MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES,
  MAXIMUM_VIDEO_FILE_SIZE_IN_MEGA_BYTES,
  megaBytesToBytes,
} from "@/helpers/file-size";
import { mergeRefs } from "@/helpers/mergeRefs";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { InputTooltip } from "../InputTooltip/InputTooltip";
import { useInputTooltip } from "../InputTooltip/useInputTooltip";
import { CommentFieldActions } from "./components/CommentFieldActions";
import { CommentFieldAvatar } from "./components/CommentFieldAvatar";
import { CommentFieldDocumentPreview } from "./components/CommentFieldDocumentPreview";
import { CommentFieldGallery } from "./components/CommentFieldGallery";
import { CommentFieldInlineCancel } from "./components/CommentFieldInlineCancel";
import { CommentFieldInput } from "./components/CommentFieldInput";
import { CommentFieldInputBox } from "./components/CommentFieldInputBox";
import { CommentFieldSend } from "./components/CommentFieldSend";
import { CommentFieldVideoPreview } from "./components/CommentFieldVideoPreview";

export type BaseCommentFieldProps = {
  value: string;
  tooltips?: {
    send?: string;
    uploadImage?: string;
    uploadVideo?: string;
    uploadDocument?: string;
  };
  isEdit?: boolean;
  commentType?: CommentType;
  disabled?: boolean;
  allowedAttachments?: ("image" | "video" | "document")[];
  images?: FormImage[];
  videos?: FormVideo[];
  documents?: FormDocument[];
  tooltip?: (params: { onClose: () => void }) => React.ReactNode;
  onSubmit: () => Promise<void>;
  onChange: (value: string) => void;
  onAddImages?: (images: FileList) => void;
  onRemoveImage?: (image: FormImage) => void;
  onAddVideo?: (video: FileList) => void;
  onRemoveVideo?: (video: FormVideo) => void;
  onAddDocument?: (document: FileList) => void;
  onRemoveDocument?: (document: FormDocument) => void;
  onCancel?: () => void;
} & Pick<React.InputHTMLAttributes<HTMLInputElement>, "autoFocus" | "placeholder">;

export interface CommentFieldWithAvatarProps extends BaseCommentFieldProps {
  user: UserDto;
  isReply?: boolean;
  actionComponent?: React.ReactNode;
  ref?: React.Ref<HTMLTextAreaElement>;
}

export function CommentField({ ref, ...props }: CommentFieldWithAvatarProps): React.ReactNode {
  const [isSubmitting, setSubmitting] = useBool();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  async function onSubmit() {
    try {
      setSubmitting.setTrue();
      await props.onSubmit();
    } finally {
      setSubmitting.setFalse();
    }
  }

  function onAddDocument(documents: FileList) {
    const maxDocumentFileSize = megaBytesToBytes(MAXIMUM_DOCUMENT_FILE_SIZE_IN_MEGA_BYTES);
    if (validateFileSizes(documents, maxDocumentFileSize)) {
      props.onAddDocument?.(documents);
    } else {
      showFlashToast({
        title: t("components.form.error.file-too-big", {
          sizeInMegaBytes: maxDocumentFileSize,
        }),
        type: "error",
      });
    }
  }

  function onAddVideo(videos: FileList) {
    const maxVideoFileSize = megaBytesToBytes(MAXIMUM_VIDEO_FILE_SIZE_IN_MEGA_BYTES);
    if (validateFileSizes(videos, maxVideoFileSize)) {
      props.onAddVideo?.(videos);
    } else {
      showFlashToast({
        title: t("components.form.error.file-too-big", {
          sizeInMegaBytes: maxVideoFileSize,
        }),
        type: "error",
      });
    }
  }

  function onAddImages(images: FileList) {
    const maxImageFileSize = megaBytesToBytes(MAXIMUM_IMAGE_FILE_SIZE_IN_MEGA_BYTES);
    if (validateFileSizes(images, maxImageFileSize)) {
      props.onAddImages?.(images);
    } else {
      showFlashToast({
        title: t("components.form.error.file-too-big", {
          sizeInMegaBytes: maxImageFileSize,
        }),
        type: "error",
      });
    }
  }

  const tooltip = useInputTooltip();

  const isContentAvailable = !!props.value && props.value.length > 0;
  const isImageAttachmentAvailable = !!props.images && props.images.length > 0;
  const isDocumentAttachmentAvailable = !!props.documents && props.documents.length > 0;
  const isVideoAttachmentAvailable = !!props.videos && props.videos.length > 0;
  const isAttachment = isImageAttachmentAvailable || isDocumentAttachmentAvailable || isVideoAttachmentAvailable;
  const canSendComment = isContentAvailable || isAttachment;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="relative flex flex-col items-end gap-2 md:flex-row md:items-center">
        <div className="flex w-full items-start gap-2">
          <div
            className={twJoin(
              "flex items-center",
              props.commentType === "public" && props.actionComponent ? "h-10" : "h-[52px]",
            )}
          >
            <CommentFieldAvatar avatar={props.user.avatar} isDeleted={!!props.user.deletedAt} />
          </div>
          <div className="flex w-full flex-col items-center gap-2">
            {props.commentType === "public" && props.actionComponent}
            <CommentFieldInputBox
              isSubmitting={isSubmitting}
              isNote={props.commentType === "internal"}
              disabled={props.disabled}
            >
              <InputTooltip tooltip={tooltip}>{props.tooltip?.({ onClose: tooltip.close })}</InputTooltip>
              <CommentFieldInput
                ref={mergeRefs(tooltip.refs.setReference, ref)}
                autoFocus={props.autoFocus}
                value={props.value}
                isEdit={props.isEdit}
                isSubmitting={isSubmitting}
                onChange={props.onChange}
                isNote={props.commentType === "internal"}
                placeholder={props.placeholder}
                disabled={props.disabled}
                {...(props.tooltip ? tooltip.getReferenceProps() : {})}
              />
              <div
                className={twResolve(
                  "flex h-10 items-center self-end bg-transparent text-grey-700 xl:self-start",
                  props.commentType === "internal" && "bg-yellow-100",
                  isSubmitting && "pointer-events-none text-grey-300",
                  props.commentType === "internal" && isSubmitting && "bg-yellow-100/30",
                )}
              >
                {props.allowedAttachments?.includes("image") && (
                  <CommentFieldActions.AddImage
                    isSubmitting={isSubmitting}
                    disabled={
                      props.disabled ||
                      (props.documents && props.documents.length > 0) ||
                      (props.videos && props.videos.length > 0)
                    }
                    tooltip={props.tooltips?.uploadImage}
                    {...{
                      onAddImages,
                    }}
                  />
                )}
                {props.allowedAttachments?.includes("video") && (
                  <CommentFieldActions.AddVideo
                    isSubmitting={isSubmitting}
                    disabled={
                      props.disabled ||
                      (props.documents && props.documents.length > 0) ||
                      (props.images && props.images.length > 0)
                    }
                    tooltip={props.tooltips?.uploadVideo}
                    {...{
                      onAddVideo,
                    }}
                  />
                )}
                {props.allowedAttachments?.includes("document") && (
                  <CommentFieldActions.AddDocument
                    isSubmitting={isSubmitting}
                    disabled={
                      props.disabled ||
                      (props.images && props.images.length > 0) ||
                      (props.videos && props.videos.length > 0)
                    }
                    tooltip={props.tooltips?.uploadDocument}
                    {...{
                      onAddDocument,
                    }}
                  />
                )}
                {!props.isReply && (
                  <div className="flex flex-row items-center gap-2">
                    <CommentFieldSend
                      isSubmitting={isSubmitting}
                      isEdit={props.isEdit}
                      canSend={canSendComment}
                      sendTooltip={props.tooltips?.send}
                      {...{
                        onSubmit,
                      }}
                    />
                    {props.onCancel && (
                      <CommentFieldInlineCancel size="sm" onCancel={props.onCancel} isCancellable={props.isEdit} />
                    )}
                  </div>
                )}
              </div>
            </CommentFieldInputBox>
          </div>
        </div>

        {props.isReply && (
          <CommentFieldSend
            isReply
            isSubmitting={isSubmitting}
            isEdit={props.isEdit}
            canSend={canSendComment}
            sendTooltip={props.tooltips?.send}
            {...{
              onSubmit,
            }}
          />
        )}
        {props.isReply && props.onCancel && (
          <CommentFieldInlineCancel onCancel={props.onCancel} isCancellable={props.isEdit} />
        )}
      </div>

      {isAttachment && (
        <div className="pl-12">
          {props.images && props.images.length > 0 && (
            <CommentFieldGallery images={props.images} onRemoveImage={props.onRemoveImage} />
          )}
          {props.videos && props.videos.length > 0 && (
            <CommentFieldVideoPreview video={props.videos[0]} onRemoveVideo={props.onRemoveVideo} />
          )}
          {props.documents && props.documents.length > 0 && (
            <CommentFieldDocumentPreview documents={props.documents} onRemoveDocument={props.onRemoveDocument} />
          )}
        </div>
      )}
    </div>
  );
}

function validateFileSizes(files: FileList, maxSize: number): boolean {
  return Array.from(files).every((file) => file.size <= maxSize);
}
